import { useCallback, useContext, useMemo } from 'react'
import ModalBase from 'LuxKit/Modal/ModalBase'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'

import ClickableLink from 'components/Common/Clickable/ClickableLink'
import { formatMarkers, overlapMarkers } from 'components/Common/PropertyMap/utils'
import loadable from 'components/utils/lazyImport'
import SearchMapCard from 'components/Search/SearchResultsMap/SearchMapCard/SearchMapCard'
import useOfferList from 'hooks/Offers/useOfferList'
import useOffers from 'hooks/Offers/useOffers'
import ModalContext from 'contexts/ModalContext'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { isBundleOffer } from 'lib/offer/offerTypes'
import offerPageURL from 'lib/offer/offerPageURL'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'
import { NEARBY_MAP_MARKER_TYPE, PROPERTY_MAP_MARKER_TYPE } from 'constants/map'
import { hasMapCoordinates } from './mapCoordinates'

const PropertyMap = loadable(() => import(/* webpackChunkName: "PropertyMap" */ 'components/Common/PropertyMap'))

interface Props {
  offer: App.Offer
  height?: number
}

const NEARBY_OFFERS_LIMIT = 10
const NEARBY_OFFER_TYPES: Array<App.OfferListOfferType> = [
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
]

const StyledClickableLink = styled(ClickableLink)`
  pointer-events: auto;
  width: 100%;
`

function OfferLocationMap({ offer, height = 258 }: Props) {
  const { property, id: offerId } = offer
  const bundledOffers = isBundleOffer(offer) ? offer.bundledOffers : undefined
  const [mapInViewRef, hasMapBeenInView] = useInView({ triggerOnce: true })
  const showModal = useContext(ModalContext)
  const isMobile = useScreenSizeOnly('mobile')

  const nearbyFilters = useMemo<App.OfferListFilters | undefined>(() => {
    if (!property?.id || bundledOffers) {
      return undefined
    }

    return {
      propertyId: `le:${property.id}`,
      include: ['nearby'],
      limit: NEARBY_OFFERS_LIMIT,
      isStream: true,
      offerTypes: NEARBY_OFFER_TYPES,
    }
  }, [bundledOffers, property?.id])

  const nearbyOfferList = useOfferList(nearbyFilters, {
    disabled: !nearbyFilters || !hasMapBeenInView,
    excludedIds: offerId ? [offerId] : undefined,
  })
  const [loadedNearbyOffers] = useOffers<App.Offer>(nearbyOfferList.offerIds)

  const nearbyOffers = useMemo(() => {
    return loadedNearbyOffers.filter(
      (nearbyOffer): nearbyOffer is App.Offer => hasMapCoordinates(nearbyOffer?.property),
    )
  }, [loadedNearbyOffers])

  const markers = useMemo<Array<App.MapMarker>>(() => {
    const result: Array<App.MapMarker> = []

    if (bundledOffers) {
      Object.values(bundledOffers).forEach((bundleOffer) => {
        result.push({
          id: bundleOffer.property.id,
          type: PROPERTY_MAP_MARKER_TYPE,
          latitude: bundleOffer.property.latitude,
          longitude: bundleOffer.property.longitude,
          name: bundleOffer.property.name,
          address: bundleOffer.property.address,
        })
      })

      return result
    }

    if (property && hasMapCoordinates(property)) {
      result.push({
        id: property.id,
        type: PROPERTY_MAP_MARKER_TYPE,
        latitude: property.latitude,
        longitude: property.longitude,
        name: property.name,
        address: property.address,
      })
    }

    nearbyOffers.forEach((nearbyOffer) => {
      if (!nearbyOffer.property || nearbyOffer.property.id === property?.id) {
        return
      }

      result.push({
        id: nearbyOffer.id,
        type: NEARBY_MAP_MARKER_TYPE,
        latitude: nearbyOffer.property.latitude,
        longitude: nearbyOffer.property.longitude,
        imageId: nearbyOffer.image.id,
        name: nearbyOffer.name,
        subName: nearbyOffer.property.name,
        locationHeading: nearbyOffer.locationHeading,
        locationSubheading: nearbyOffer.locationSubheading,
        address: nearbyOffer.property.address,
        openGoogleMapsOnSelect: false,
        offer: nearbyOffer,
      })
    })

    return overlapMarkers(formatMarkers(result))
  }, [bundledOffers, nearbyOffers, property])

  const renderNearbyOfferCard = useCallback((
    nearbyOffer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary,
    onCloseClick: () => void,
  ) => {
    return (
      <StyledClickableLink
        to={offerPageURL(nearbyOffer)}
        preventSwipeClick
      >
        <SearchMapCard
          offer={nearbyOffer}
          onClose={onCloseClick}
        />
      </StyledClickableLink>
    )
  }, [])

  const getNearbyOfferFromMarker = useCallback((marker: App.MapMarker) => {
    if (!marker.offer) {
      return undefined
    }

    return marker.offer as
      | App.HotelOffer
      | App.HotelOfferSummary
      | App.BedbankOffer
      | App.BedbankOfferSummary
  }, [])

  const renderInfoWindow = useCallback((marker: App.MapMarker, onCloseClick: () => void) => {
    const nearbyOffer = getNearbyOfferFromMarker(marker)
    if (!nearbyOffer) {
      return null
    }

    return renderNearbyOfferCard(nearbyOffer, onCloseClick)
  }, [getNearbyOfferFromMarker, renderNearbyOfferCard])

  const handleSelectMarker = useCallback((marker: App.MapMarker) => {
    if (!isMobile) {
      return
    }

    const nearbyOffer = getNearbyOfferFromMarker(marker)
    if (!nearbyOffer) {
      return
    }

    showModal((resolve) => (
      <ModalBase mode="default" height="auto" onClose={() => resolve()}>
        {renderNearbyOfferCard(nearbyOffer, () => resolve())}
      </ModalBase>
    ))
  }, [getNearbyOfferFromMarker, isMobile, renderNearbyOfferCard, showModal])

  return (
    <div ref={mapInViewRef}>
      <PropertyMap
        height={height}
        wrapperStyle={{ height }}
        markers={markers}
        focusOnFirstMarker={!bundledOffers}
        disablePanOnMarkerSelect
        enableNearbyMarkerFullscreen={!isMobile}
        highlightedMarkerId={bundledOffers ? undefined : property?.id}
        onSelectMarker={bundledOffers ? undefined : handleSelectMarker}
        renderInfoWindow={bundledOffers || isMobile ? undefined : renderInfoWindow}
      />
    </div>
  )
}

export default OfferLocationMap
